/**
 * URLs safe to put in an `href`, hand to `window.open`, or navigate to.
 *
 * `safeHref`/`openExternal` are ported from Conduit `frontend/src/lib/safeUrl.ts` (#886): a deny-list,
 * because the fleet's links use many launcher schemes (ssh:, vnc:, rdp:, winbox:). Citations, Conduit
 * links and Riley's sources render through these.
 *
 * `safeReturnTo` and `zohoAuthorizeUrl` are Patchbay's own: the sign-in round trip must never become an
 * open redirect, and the browser is only ever sent to an allow-listed Zoho accounts host (the same list
 * the API holds in `ZOHO_ALLOWED_ACCOUNTS_HOSTS`, multi-DC).
 */

const UNSAFE_SCHEMES = ['javascript:', 'vbscript:', 'data:', 'file:', 'blob:']

// Browsers drop ASCII tab/newline anywhere in a URL, and controls/space at its ends, before reading the
// scheme, so "java\tscript:" is still javascript:.
function squash(url: string): string {
  let out = ''
  for (const ch of url) {
    const code = ch.charCodeAt(0)
    if (code > 0x20 && code !== 0x7f) out += ch
  }
  return out.toLowerCase()
}

/** The URL itself, or `undefined` when it would execute in the app's origin. */
export function safeHref(url: string | null | undefined): string | undefined {
  if (!url) return undefined
  const squashed = squash(url)
  return UNSAFE_SCHEMES.some((s) => squashed.startsWith(s)) ? undefined : url
}

/** `window.open` in a new tab, for a URL that passes {@link safeHref} only. */
export function openExternal(url: string | null | undefined): void {
  const href = safeHref(url)
  if (href) window.open(href, '_blank', 'noopener,noreferrer')
}

/** Same-origin path only ("/queue/reply-owed?x=1"); anything else becomes "/". Mirrors the API's `_safe_return_to`. */
export function safeReturnTo(value: string | null | undefined): string {
  if (!value) return '/'
  const squashed = squash(value)
  if (!value.startsWith('/') || squashed.startsWith('//') || value.includes('\\') || squashed.startsWith('/\\')) return '/'
  // Never bounce back into the sign-in screen itself.
  if (value === '/signin' || value.startsWith('/signin?') || value.startsWith('/signin/')) return '/'
  return value
}

export const ZOHO_ACCOUNTS_HOSTS = [
  'accounts.zoho.com',
  'accounts.zoho.eu',
  'accounts.zoho.in',
  'accounts.zoho.com.au',
  'accounts.zoho.jp',
  'accounts.zohocloud.ca',
  'accounts.zoho.sa',
  'accounts.zoho.uk',
] as const

/** The authorize URL from `/auth/zoho/start` or `/auth/desk/connect`, if it is https on an allow-listed Zoho host. */
export function zohoAuthorizeUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null
  let u: URL
  try {
    u = new URL(value)
  } catch {
    return null
  }
  if (u.protocol !== 'https:' || u.username || u.password || u.port) return null
  return (ZOHO_ACCOUNTS_HOSTS as readonly string[]).includes(u.hostname) ? u.toString() : null
}
